"use client";

import { useEffect, useRef } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { getComplexityName } from "./Assessment/ApiDetailsModal";

interface MigrationDistributionChartProps {
  data: {
    labels: string[];
    values: number[];
    colors: string[];
  };
  showTitle?: boolean;
}

export function MigrationDistributionChart({ data, showTitle = false }: MigrationDistributionChartProps) {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<any>(null);
  const chartData = data;

  useEffect(() => {
    // Importamos Chart.js dinámicamente para evitar problemas de tipado
    const loadChart = async () => {
      if (!chartRef.current) return;

      // Destruir el gráfico anterior si existe
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }

      try {
        // Importar Chart.js dinámicamente sin tipos explícitos
        const chartModule = await import("chart.js");
        const { Chart, ArcElement, Tooltip, Legend, DoughnutController, CategoryScale } = chartModule;

        // Registrar los componentes necesarios
        Chart.register(ArcElement, Tooltip, Legend, DoughnutController, CategoryScale);

        const ctx = chartRef.current.getContext("2d");
        if (!ctx) return;

        // Crear el gráfico con configuración básica
        chartInstanceRef.current = new Chart(ctx, {
          type: "doughnut",
          data: {
            labels: chartData.labels,
            datasets: [
              {
                data: chartData.values,
                backgroundColor: chartData.colors,
                // color: chartData.colors,
                borderWidth: 0,
                hoverOffset: 5,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: "65%", // Esto funcionará con la importación dinámica
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label: (context: any) => {
                    const value = context.formattedValue;
                    const total = context.dataset.data.reduce((a: number, b: number) => Number(a) + Number(b), 0);
                    const percentage = Math.round((Number(context.raw) / total) * 100);
                    return `${value} - (${percentage}%)`;
                  },
                },
              },
            },
          },
        });
      } catch (error) {
        console.error("Error loading Chart.js:", error);

        // Si falla la carga de Chart.js, renderizamos un gráfico alternativo simple
        renderSimpleChart();
      }
    };

    // Función para renderizar un gráfico simple sin Chart.js
    const renderSimpleChart = () => {
      const canvas = chartRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Limpiar el canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Dibujar un gráfico simple
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const radius = Math.min(centerX, centerY) - 10;

      let startAngle = 0;
      const total = chartData.values.reduce((sum, value) => sum + value, 0);

      chartData.values.forEach((value, index) => {
        const sliceAngle = (value / total) * 2 * Math.PI;

        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, startAngle, startAngle + sliceAngle);
        ctx.closePath();

        ctx.fillStyle = chartData.colors[index];
        ctx.fill();

        startAngle += sliceAngle;
      });

      // Dibujar un círculo blanco en el centro para crear efecto de dona
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius * 0.65, 0, 2 * Math.PI);
      ctx.fillStyle = "white";
      ctx.fill();
    };

    loadChart();

    // Cleanup function
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, []);

  // Calculamos el total para los porcentajes en la leyenda
  const total = chartData.values.reduce((sum, value) => sum + value, 0);

  return (
    <Paper variant="outlined" sx={{ p: 2, height: "auto", minHeight: "250px", position: "relative" }}>
      {/* Título opcional - controlado por prop */}
      {showTitle && (
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Migration Distribution
        </Typography>
      )}

      <Box sx={{ position: "relative", width: "100%", height: "180px" }}>
        <canvas ref={chartRef} />
      </Box>

      {/* Leyenda con línea superior */}
      <Box
        sx={{
          mt: 1,
          pt: 1,
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 2,
            mt: 0.5,
            pb: 1, // Añadir padding en la parte inferior
          }}
        >
          {chartData.labels.map((label, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "2px",
                  bgcolor: chartData.colors[index],
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  whiteSpace: "nowrap",
                  fontSize: "0.75rem",
                }}
              >
                {label}: {Math.round((chartData.values[index] / total) * 100)}%
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
}
